import React, { useState, useRef, useEffect } from "react";
import {
  PlusFill,
  More,
  Chevron,
  ChatLine,
  Document,
  AiLine,
} from "../../assets/svgs/icons";

export interface MenuItemProps {
  date?: boolean;
  dateText?: string;
  type?: "btn" | "menuitem" | "submenuitem";
  state?: "default" | "hover";
  label?: string;
  icon?: "chatline" | "document" | "ailine"; // 메뉴 아이템 아이콘 타입
  onClick?: () => void;
  onMoreClick?: (e: React.MouseEvent) => void;
  showMore?: boolean;
  // 편집 메뉴 관련
  onEdit?: () => void;
  onDelete?: () => void;
  onSave?: () => void; // 보관함에 저장
  isStorage?: boolean; // 보관함 항목인지 여부
  isEditing?: boolean; // 편집 모드인지 여부
  onTitleChange?: (newTitle: string) => void;
  canSave?: boolean; // 보관 가능 여부 (소크라테스의 경우 is_completed 체크)
  disabled?: boolean; // 비활성화 여부 (스트리밍 중 등)
}

const MenuItem: React.FC<MenuItemProps> = ({
  date = false,
  dateText = "최근 7일",
  type = "btn",
  state = "default",
  label = "새 튜터",
  icon = "chatline",
  onClick,
  onMoreClick,
  showMore = true,
  // 편집 메뉴
  onEdit,
  onDelete,
  onSave,
  isStorage = false,
  isEditing = false,
  onTitleChange,
  canSave = true,
  disabled = false,
}) => {
  const [showEditMenu, setShowEditMenu] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 편집 모드 진입 시 input에 포커스
  useEffect(() => {
    if (isEditing && inputRef.current) {
      setEditValue(label || "");
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // 편집 메뉴 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowEditMenu(false);
      }
    };

    if (showEditMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showEditMenu]);

  // 제목 변경 완료
  const handleTitleSubmit = () => {
    if (editValue.trim() && onTitleChange) {
      onTitleChange(editValue.trim());
    }
  };

  // Enter 키 처리
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleTitleSubmit();
    }
  };
  // btn type - "새 튜터" button
  if (type === "btn") {
    return (
      <button
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={`box-border flex items-center px-5 py-3 md:py-[18px] gap-2 md:gap-3 w-full relative group transition-colors hover:bg-transparent ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        data-name={`type=btn, state=${state}`}
      >
        <div
          className={`absolute bg-[#f8f5fe] h-10 rounded-[20px] top-1/2 -translate-y-1/2 left-3 right-2 opacity-0 ${
            disabled ? "" : "group-hover:opacity-100"
          } transition-opacity`}
        />
        <span className="relative shrink-0 flex items-center justify-center overflow-clip">
          <PlusFill className="w-5 h-5 md:w-6 md:h-6" fill="#6d2ef2" />
        </span>
        <span className="relative flex-1 text-left font-bold text-[#6d2ef2] tracking-[-0.28px] md:tracking-[-0.32px] text-[14px] leading-5 md:text-[16px] md:leading-6">
          {label}
        </span>
      </button>
    );
  }

  // menuitem type - collapsible menu header
  if (type === "menuitem") {
    const IconComponent =
      icon === "document" ? Document : icon === "ailine" ? AiLine : ChatLine;

    return (
      <div
        className="box-border flex items-center h-11 md:h-12 pl-5 pr-0 gap-2 md:gap-3 w-full relative group cursor-pointer transition-colors"
        onClick={onClick}
        data-name={`type=menuitem, state=${state}`}
      >
        <div className="absolute bg-[#f7f7f7] h-10 rounded-[20px] top-[2px] md:top-1 left-3 right-2 opacity-0 md:group-hover:opacity-100 transition-opacity" />
        <span className="relative overflow-clip shrink-0 flex items-center justify-center">
          <IconComponent className="w-5 h-5 md:w-6 md:h-6" fill="#212121" />
        </span>
        <span className="relative flex-1 font-semibold text-left text-[#212121] tracking-[-0.42px] text-[14px] leading-5">
          {label || "튜터 목록"}
        </span>
        <div className="relative box-border flex gap-[10px] items-center justify-center p-[10px] md:p-3 shrink-0 size-11 md:size-12">
          <Chevron
            width={24}
            height={24}
            className="text-[#212121] menu-chevron transition-transform"
          />
        </div>
      </div>
    );
  }

  // submenuitem type - individual chat history item
  if (type === "submenuitem") {
    return (
      <div
        className={`flex flex-col items-start w-full ${
          disabled ? "opacity-50" : ""
        }`}
      >
        {date && (
          <div className="box-border flex gap-[10px] h-8 items-center pb-1 pt-3 px-5 relative shrink-0 w-full">
            <span className="flex-1 font-semibold text-[#212121] text-[12px] tracking-[-0.24px] leading-4">
              {dateText}
            </span>
          </div>
        )}
        <div
          className={`box-border flex items-center h-10 md:h-11 pl-5 pr-1 gap-2 md:gap-3 relative shrink-0 w-full group transition-colors ${
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          }`}
          onClick={isEditing || disabled ? undefined : onClick}
          data-name={`type=submenuitem, state=${state}`}
        >
          <div
            className={`absolute bg-[#f7f7f7] h-10 rounded-[20px] top-0 md:top-[2px] left-3 right-2 opacity-0 ${
              disabled ? "" : "md:group-hover:opacity-100"
            } transition-opacity`}
          />

          {/* 편집 모드: input */}
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleTitleSubmit}
              onKeyDown={handleKeyDown}
              className="relative flex-1 font-normal text-[#212121] tracking-[-0.28px] text-[14px] leading-5 bg-white border border-[#6d2ef2] rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#6d2ef2]"
            />
          ) : (
            /* 일반 모드: label */
            <span className="relative flex-1 font-normal text-[#212121] tracking-[-0.28px] text-[14px] leading-5 overflow-hidden text-ellipsis whitespace-nowrap">
              {label || "화성에서 생명체가 살 수 있을까요?"}
            </span>
          )}

          {/* More 버튼 & 편집 메뉴 */}
          {showMore && !isEditing && !disabled && (
            <div className="relative shrink-0 size-10" ref={menuRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEditMenu(!showEditMenu);
                  onMoreClick?.(e);
                }}
                className="absolute box-border flex gap-[10px] items-center justify-center left-0 p-[10px] size-10 top-0 hover:bg-[#f7f7f7] rounded-full transition-colors"
              >
                <More width={20} height={20} className="text-[#212121]" />
              </button>

              {/* 편집 메뉴 Popover */}
              {/* {showEditMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-[#eeeeee] rounded-lg shadow-lg py-1 z-50 min-w-[120px]">
                  {!isStorage && onEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                        setShowEditMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-[#212121] hover:bg-[#f7f7f7] transition-colors"
                    >
                      편집
                    </button>
                  )}

                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                        setShowEditMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-[#212121] hover:bg-[#f7f7f7] transition-colors"
                    >
                      삭제
                    </button>
                  )}

                  {!isStorage && onSave && canSave && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSave();
                        setShowEditMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-[#212121] hover:bg-[#f7f7f7] transition-colors"
                    >
                      보관
                    </button>
                  )}
                </div>
              )} */}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default MenuItem;
